/**
 * Shot records: what a shot actually did, as data.
 *
 * `analyzeFlight` reads a flown trajectory against the planets and reports,
 * per body, how close the shot came and how much it bent the path — the
 * ingredients of a trick shot. `buildShotRecord` adds the outcome (impact,
 * detonation, lost, hitscan) and what the blast did. Records accumulate on
 * `GameState.shotRecords` and feed the after-shot readout (`describeShot`),
 * highlight detection, end-of-match stats, and the bot-vs-bot harness.
 *
 * Pure and deterministic: the trajectory is the game's own precomputed
 * flight, so both P2P peers produce identical records.
 */

import type { Vec3, PlanetState, FireParams, WeaponKey, GameState, ShotRecord, ShotBodyPass, ShotOutcome } from '../types';
import { dacos } from './dmath';

/** A body's gravitational "influence" for assist detection, in radii from its centre. */
export const ASSIST_INFLUENCE_RADII = 3;
/** Minimum heading change inside the influence sphere to call it an assist. */
export const ASSIST_MIN_DEFLECTION = (20 * Math.PI) / 180;
/** Closest approach (to the surface, in radii) within which a bend counts as an assist. */
export const ASSIST_MAX_APPROACH_RADII = 1.0;

export interface FlightAnalysis {
  bodies: ShotBodyPass[];
  assists: number;
  flightSteps: number;
}

function headingAngle(ax: number, ay: number, az: number, bx: number, by: number, bz: number): number {
  const la = Math.sqrt(ax * ax + ay * ay + az * az);
  const lb = Math.sqrt(bx * bx + by * by + bz * bz);
  if (la < 1e-12 || lb < 1e-12) return 0;
  const c = (ax * bx + ay * by + az * bz) / (la * lb);
  return dacos(Math.max(-1, Math.min(1, c)));
}

/**
 * Per-body closest approach and deflection along a flown path. The launch
 * planet and the planet the shot ended on are reported but never count as
 * assists; stars and wormholes are skipped.
 */
export function analyzeFlight(
  points: Vec3[],
  planets: PlanetState[],
  launchPlanetIndex: number,
  endPlanetIndex: number,
): FlightAnalysis {
  const bodies: ShotBodyPass[] = [];
  const n = points.length;
  let assists = 0;

  for (let pi = 0; pi < planets.length; pi++) {
    const planet = planets[pi];
    if (!planet.alive || planet.type !== 'planet') continue;
    const c = planet.position;
    const influence = planet.radius * ASSIST_INFLUENCE_RADII;

    let closest = Infinity;
    // Heading on entering / leaving the influence sphere (first entry only)
    let entryIdx = -1;
    let exitIdx = -1;
    let inside = false;
    for (let i = 0; i < n; i++) {
      const p = points[i];
      const dx = p.x - c.x, dy = p.y - c.y, dz = p.z - c.z;
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz) - planet.radius;
      if (d < closest) closest = d;
      const within = d + planet.radius <= influence;
      if (within && !inside) { inside = true; if (entryIdx < 0) entryIdx = i; }
      else if (!within && inside) { inside = false; if (exitIdx < 0 && entryIdx >= 0) exitIdx = i; }
    }
    if (entryIdx >= 0 && exitIdx < 0) exitIdx = n - 1; // still inside when the flight ended

    let deflection = 0;
    if (entryIdx >= 1 && exitIdx > entryIdx) {
      const a0 = points[entryIdx - 1], a1 = points[entryIdx];
      const b0 = points[exitIdx - 1], b1 = points[exitIdx];
      deflection = headingAngle(a1.x - a0.x, a1.y - a0.y, a1.z - a0.z, b1.x - b0.x, b1.y - b0.y, b1.z - b0.z);
    }

    const isEndpoint = pi === launchPlanetIndex || pi === endPlanetIndex;
    const assist = !isEndpoint && entryIdx >= 0
      && closest <= planet.radius * ASSIST_MAX_APPROACH_RADII
      && deflection >= ASSIST_MIN_DEFLECTION;
    if (assist) assists++;
    bodies.push({ planetIndex: pi, closestApproach: n > 0 ? closest : Infinity, deflection, assist });
  }

  return { bodies, assists, flightSteps: Math.max(0, n - 1) };
}

export interface ShotRecordInput {
  turnNumber: number;
  shooterId: number;
  weaponKey: WeaponKey;
  params: FireParams;
  submunition: boolean;
  outcome: ShotOutcome;
  launchPlanetIndex: number;
  /** Points actually flown (playback cursor or full flight). Empty for hitscan. */
  points: Vec3[];
  planets: PlanetState[];
  impactPlanetIndex: number;
  impactPoint: Vec3 | null;
  intendedTarget?: Vec3 | null;
  /** Blast damage per player, as resolveImpact reports it. */
  damages: Array<{ playerId: number; damage: number }>;
  kills: number;
}

export function buildShotRecord(input: ShotRecordInput): ShotRecord {
  const flight = analyzeFlight(input.points, input.planets, input.launchPlanetIndex, input.impactPlanetIndex);
  let damage = 0;
  let selfDamage = 0;
  for (const d of input.damages) {
    damage += d.damage;
    if (d.playerId === input.shooterId) selfDamage += d.damage;
  }
  let missDistance: number | null = null;
  if (input.intendedTarget && input.impactPoint) {
    const dx = input.impactPoint.x - input.intendedTarget.x;
    const dy = input.impactPoint.y - input.intendedTarget.y;
    const dz = input.impactPoint.z - input.intendedTarget.z;
    missDistance = Math.sqrt(dx * dx + dy * dy + dz * dz);
  }
  return {
    turnNumber: input.turnNumber,
    shooterId: input.shooterId,
    weaponKey: input.weaponKey,
    params: input.params,
    submunition: input.submunition,
    outcome: input.outcome,
    launchPlanetIndex: input.launchPlanetIndex,
    impactPlanetIndex: input.impactPlanetIndex,
    impactPoint: input.impactPoint ? { ...input.impactPoint } : null,
    flightSteps: flight.flightSteps,
    bodies: flight.bodies,
    assists: flight.assists,
    missDistance,
    damage,
    kills: input.kills,
    selfDamage,
  };
}

/** Append a record to the match history (immutable). */
export function appendShotRecord(state: GameState, record: ShotRecord): GameState {
  return { ...state, shotRecords: [...(state.shotRecords ?? []), record] };
}

/** Seconds of flight at the game's fixed physics step. */
export function shotFlightSeconds(record: ShotRecord, physDt: number): number {
  return record.flightSteps * physDt;
}

/** A shot worth a replay: a damaging hit that bent around at least one body, or a very long hit. */
export function isHighlightShot(record: ShotRecord, physDt: number): boolean {
  if (record.outcome !== 'impact' && record.outcome !== 'playerHit') return false;
  if (record.damage <= 0) return false;
  return record.assists >= 1 || shotFlightSeconds(record, physDt) >= 20;
}

/** One line for the log, e.g. "Shot report: 2 assists · 18.4s flight · 0.6u off target · 35 dmg". */
export function describeShot(record: ShotRecord, physDt: number): string {
  const parts: string[] = [];
  if (record.outcome === 'hitscan') {
    parts.push(record.damage > 0 ? `${record.damage} dmg` : 'miss');
    return `Shot report: ${parts.join(' · ')}`;
  }
  if (record.assists > 0) parts.push(`${record.assists} assist${record.assists === 1 ? '' : 's'}`);
  parts.push(`${shotFlightSeconds(record, physDt).toFixed(1)}s flight`);
  if (record.outcome === 'lost') parts.push('lost in space');
  else if (record.outcome === 'detonated') parts.push('detonated');
  else if (record.outcome === 'playerHit') parts.push('direct hit');
  if (record.missDistance !== null) parts.push(`${record.missDistance.toFixed(1)}u off target`);
  if (record.damage > 0) parts.push(`${Math.round(record.damage)} dmg`);
  if (record.kills > 0) parts.push(`${record.kills} kill${record.kills === 1 ? '' : 's'}`);
  if (record.selfDamage > 0) parts.push(`${Math.round(record.selfDamage)} self`);
  return `Shot report: ${parts.join(' · ')}`;
}

// =============================================================================
// Match summary
// =============================================================================

export interface ShooterShotStats {
  playerId: number;
  shots: number;        // primary shots (bomblets excluded)
  hits: number;         // primary shots that dealt damage
  assists: number;      // gravity assists across all shots, bomblets included
  longestFlightSteps: number;
  best: ShotRecord | null;  // the shooter's best primary shot by shotScore
}

/** Rank a shot for "shot of the match": assists first, then damage, then flight length. -1 for anything that did not land. */
export function shotScore(record: ShotRecord): number {
  if (record.outcome !== 'impact' && record.outcome !== 'playerHit') return -1;
  return record.assists * 1000 + record.damage * 10 + record.flightSteps / 1000;
}

export function summarizeShots(records: ShotRecord[]): Map<number, ShooterShotStats> {
  const out = new Map<number, ShooterShotStats>();
  for (const r of records) {
    let s = out.get(r.shooterId);
    if (!s) { s = { playerId: r.shooterId, shots: 0, hits: 0, assists: 0, longestFlightSteps: 0, best: null }; out.set(r.shooterId, s); }
    if (!r.submunition) s.shots++;
    if (r.damage > 0 && !r.submunition) s.hits++;
    s.assists += r.assists;
    if (r.flightSteps > s.longestFlightSteps) s.longestFlightSteps = r.flightSteps;
    if (!r.submunition && shotScore(r) >= 0 && shotScore(r) > (s.best ? shotScore(s.best) : -1)) s.best = r;
  }
  return out;
}

/** The single best primary shot of the match, or null when nothing landed. */
export function shotOfTheMatch(records: ShotRecord[]): ShotRecord | null {
  let best: ShotRecord | null = null;
  for (const r of records) {
    if (r.submunition) continue;
    const sc = shotScore(r);
    if (sc < 0) continue;
    if (!best || sc > shotScore(best)) best = r;
  }
  return best;
}
